import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

export const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('-createdAt');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch clients with pagination, search, and filters
  const fetchClients = async () => {
    setLoading(true);
    try {
      // بناء query parameters
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        sort: sortBy,
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus !== 'all' && { isActive: filterStatus })
      });

      const response = await fetch(`http://localhost:8000/api/v1/clients?${params}`);
      const data = await response.json();

      if (response.ok) {
        setClients(data.data.clients);
        setTotalCount(data.pagination.total || data.results);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, rowsPerPage, sortBy, filterStatus, searchTerm]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        setPage(0);
        fetchClients();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleRefresh = () => {
    setPage(0);
    setSearchTerm('');
    setFilterStatus('all');
    setSortBy('-createdAt');
    fetchClients();
  };

  // Mobile Card View
  const MobileCard = ({ client }) => (
    <Card sx={{ mb: 2, borderRadius: '10px' }}>
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">
              {client.name}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {client.email}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              الهاتف
            </Typography>
            <Typography variant="body2">
              {client.phonecode} {client.phonenumber}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              الحالة
            </Typography>
            <Chip
              label={client.isActive ? 'نشط' : 'غير نشط'}
              size="small"
              color={client.isActive ? 'success' : 'default'}
              sx={{ height: 24 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">
              الموضوع
            </Typography>
            <Typography variant="body2" noWrap>
              {client.subject}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(client.createdAt), 'dd MMMM yyyy', { locale: ar })}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: '10px' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          إدارة العملاء
        </Typography>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="بحث بالاسم أو البريد الإلكتروني..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} edge="end" size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>الحالة</InputLabel>
              <Select
                value={filterStatus}
                label="الحالة"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="true">نشط</MenuItem>
                <MenuItem value="false">غير نشط</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>ترتيب حسب</InputLabel>
              <Select
                value={sortBy}
                label="ترتيب حسب"
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="-createdAt">الأحدث</MenuItem>
                <MenuItem value="createdAt">الأقدم</MenuItem>
                <MenuItem value="name">الاسم</MenuItem>
                <MenuItem value="-name">الاسم (عكسي)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table/Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {isMobile ? (
            // Mobile View - Cards
            <Box>
              {clients.map((client) => (
                <MobileCard key={client._id} client={client} />
              ))}
            </Box>
          ) : (
            // Desktop View - Table
            <TableContainer component={Paper} sx={{ borderRadius: '10px' }}>
              <Table>
                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>الاسم</TableCell>
                    <TableCell>البريد الإلكتروني</TableCell>
                    <TableCell>رقم الهاتف</TableCell>
                    <TableCell>الموضوع</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>تاريخ التسجيل</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client, index) => (
                    <TableRow key={client._id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell fontWeight="500">{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>
                        {client.phonecode} {client.phonenumber}
                      </TableCell>
                      <TableCell>
                        <Typography noWrap sx={{ maxWidth: 200 }}>
                          {client.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.isActive ? 'نشط' : 'غير نشط'}
                          size="small"
                          color={client.isActive ? 'success' : 'default'}
                          sx={{ minWidth: 60 }}
                        />
                      </TableCell>
                      <TableCell>
                        {format(new Date(client.createdAt), 'yyyy/MM/dd')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="عدد العناصر في الصفحة"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} من ${count}`
              }
              sx={{
                '& .MuiTablePagination-select': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>
        </motion.div>
      )}
    </Box>
  );
};

export default Clients;